import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { toast } from "sonner";
import { createLimitOrder, cancelLimitOrder } from "@/lib/api";

const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const LABEL = { take_profit: "Take-profit", stop_loss: "Stop-loss", trailing_stop: "Trailing stop" };

const OpenOrder = ({ o, onCancel }) => (
  <div className="flex items-center justify-between bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 font-mono text-xs" data-testid={`open-order-${o.id.slice(0, 6)}`}>
    <span>
      <span className={o.order_type === "take_profit" ? "text-bt-positive" : "text-bt-negative"}>{LABEL[o.order_type]}</span>
      {o.order_type === "trailing_stop" && <span className="text-bt-text-tertiary"> {o.trail_pct}%</span>}
      <span className="text-bt-text-primary"> @ ${fmt(o.target_price)}</span>
      <span className="text-bt-text-tertiary"> · {o.shares ?? "all"} sh</span>
    </span>
    <button onClick={() => onCancel(o)} className="text-bt-text-tertiary hover:text-bt-negative" title="Cancel order" data-testid={`cancel-order-${o.id.slice(0, 6)}`}>
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

export const LimitOrderDialog = ({ item, orders, onClose, onChanged }) => {
  const [type, setType] = useState("take_profit");
  const [target, setTarget] = useState("");
  const [shares, setShares] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (item) { setType("take_profit"); setTarget(""); setShares(String(item.shares)); } }, [item]);
  if (!item) return null;

  const isShort = item.position_type === "short";
  const rising = (type === "take_profit") !== isShort;
  const px = parseFloat(target);
  const n = parseFloat(shares);
  const valid = px > 0 && n > 0 && n <= item.shares;
  const pnl = valid ? (isShort ? item.buy_price - px : px - item.buy_price) * n : 0;
  const mine = orders.filter(o => o.portfolio_item_id === item.id);

  const submit = async () => {
    setBusy(true);
    try {
      await createLimitOrder({ portfolio_item_id: item.id, order_type: type, target_price: px, shares: n >= item.shares ? null : n });
      toast.success(`${LABEL[type]} order placed for ${item.symbol} at $${fmt(px)}`);
      setTarget("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't place order");
    }
    setBusy(false);
  };

  const cancel = async (o) => {
    try { await cancelLimitOrder(o.id); toast.success("Order cancelled"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't cancel order"); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary w-[calc(100%-1.5rem)] sm:max-w-md" data-testid="limit-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Sell orders · {item.symbol}</DialogTitle>
          <DialogDescription className="text-bt-text-secondary text-xs">
            Live ${fmt(item.current_price)} · {item.shares} sh {isShort ? "short" : "long"} @ ${fmt(item.buy_price)}. Checked every ~90s (stocks during market hours, crypto 24/7); fills at the live price.
          </DialogDescription>
        </DialogHeader>
        {mine.length > 0 && <div className="space-y-1.5" data-testid="open-orders-list">{mine.map(o => <OpenOrder key={o.id} o={o} onCancel={cancel} />)}</div>}
        <div className="grid grid-cols-2 gap-2">
          {["take_profit", "stop_loss"].map(t => (
            <button key={t} onClick={() => setType(t)} data-testid={`order-type-${t}`}
              className={`py-2 text-xs font-mono border transition-colors ${type === t ? (t === "take_profit" ? "border-bt-positive bg-bt-positive text-bt-bg font-semibold" : "border-bt-negative bg-bt-negative text-bt-bg font-semibold") : "border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-primary"}`}>
              {LABEL[t]}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-bt-text-tertiary -mt-1" data-testid="order-type-hint">
          {isShort ? "Cover" : "Sell"} when the price {rising ? "rises to or above" : "falls to or below"} your target.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-bt-text-secondary">Target price</Label>
            <Input type="number" min="0" step="any" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={fmt(item.current_price)}
              className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="order-target-input" />
          </div>
          <div>
            <Label className="text-xs text-bt-text-secondary">Shares</Label>
            <div className="flex gap-1 mt-1">
              <Input type="number" min="0" max={item.shares} step="any" value={shares} onChange={(e) => setShares(e.target.value)}
                className="bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="order-shares-input" />
              <Button type="button" variant="outline" onClick={() => setShares(String(item.shares))} className="border-bt-border-subtle text-xs font-mono px-2" data-testid="order-all-btn">All</Button>
            </div>
          </div>
        </div>
        {valid && (
          <p className="font-mono text-xs text-bt-text-secondary" data-testid="order-preview">
            Est. P&L if filled at target: <span className={pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}>{pnl >= 0 ? "+" : "-"}${fmt(Math.abs(pnl))}</span>
          </p>
        )}
        <Button onClick={submit} disabled={!valid || busy} className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light" data-testid="order-submit-btn">
          {busy ? "Placing…" : `Place ${LABEL[type].toLowerCase()} order`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
